/**
 * Протокол «Рыночного стола» (версия 2: вход по сессии, профиль и администрирование — HTTP API).
 *
 * Единицы:
 *  - деньги — целые «минорные» единицы: 1 кр. = 100 (Minor);
 *  - цены индекса — пункты (number, кратны шагу цены 0,25);
 *  - время — миллисекунды Unix epoch по часам сервера.
 *
 * Принцип: клиент получает только то, что ему разрешено видеть на текущем этапе.
 * До этапа 3 нет тикеров; до этапа 2 бары передаются в нормированных единицах;
 * бары будущих минут не передаются никогда.
 */

export const PROTOCOL_VERSION = 2;

export type Minor = number;
export type Direction = 'up' | 'down';
export type GameMode = 'live' | 'replay';
export type DataSource = 'alpaca' | 'synthetic';

export const MINOR_PER_CREDIT = 100;

// ───────────────────────── Рынок / график ─────────────────────────

export interface Bar {
  /** начало минуты на игровой шкале времени, мс */
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  /** относительная активность агрегата (1 = средняя за окно истории) */
  v: number;
}

export interface ChartPayload {
  gameId: string;
  /** normalized — до этапа 2 (шкала скрыта); points — реальные пункты индекса */
  units: 'normalized' | 'points';
  /** P₀ в единицах units (для normalized всегда 0) */
  p0: number;
  /** P₀ в пунктах (известна с этапа 1) */
  p0Points: number;
  tick: number;
  bars: Bar[];
}

// ───────────────────────── Стол / розыгрыш ─────────────────────────

export type GameStage =
  | 'prep'      // этап 0: подготовка, взносы внесены
  | 'round1'    // график и направление
  | 'round2'    // шкала цен
  | 'round3'    // состав акций и число пунктов
  | 'locked'    // ожидание закрытия целевой свечи
  | 'settled'   // расчёт выполнен
  | 'cancelled'; // отменён, взносы возвращены

export type ActionType = 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'allin';

export type SeatStatus = 'active' | 'folded' | 'allin' | 'waiting';

export interface SeatView {
  playerId: string;
  name: string;
  seat: number;
  isBot: boolean;
  isMe: boolean;
  status: SeatStatus;
  /** доступный остаток кредитов */
  stack: Minor;
  /** ставка в текущем круге */
  roundBet: Minor;
  /** всего внесено в банк розыгрыша */
  committed: Minor;
  lastAction: { type: ActionType | 'ante'; amount: Minor; auto: boolean } | null;
  connected: boolean;
  /** открытая статистика игрока (если он разрешил её показывать) */
  stats: PublicStats | null;
}

export interface AllowedActions {
  fold: boolean;
  check: boolean;
  call: { amount: Minor; allIn: boolean } | null;
  /** суммы — итоговая ставка игрока в круге («до») */
  bet: { min: Minor; max: Minor } | null;
  raise: { min: Minor; max: Minor } | null;
  allIn: { amount: Minor } | null;
}

export type AutoMode = 'none' | 'checkFold' | 'checkOnly' | 'callUpTo';

export interface AutoAction {
  mode: AutoMode;
  /** для callUpTo: максимальная доплата */
  limit?: Minor;
}

export interface Forecast {
  direction: Direction | null;
  /** строго положительное число пунктов, кратное шагу */
  points: number | null;
}

export interface Composition {
  symbol: string;
  name: string;
}

export interface CommitmentView {
  hash: string;
  /** раскрываются только после расчёта */
  payload?: string;
  salt?: string;
}

/**
 * Публичная запись ленты ходов. Видна всем за столом: действия и суммы ставок открыты по правилам,
 * скрыты только прогнозы (направление и пункты) — до расчёта.
 */
export interface GameActionView {
  seq: number;
  at: number;
  /** круг 1–3; 0 — подготовка (взносы) */
  round: number;
  kind: 'action' | 'autoFold' | 'uncalledReturn';
  playerId: string;
  name: string;
  action?: ActionType | 'ante';
  /** ставка/райз — итоговая ставка игрока в круге; колл/олл-ин — доплата; возврат — сумма возврата */
  amount?: Minor;
  auto?: boolean;
  text: string;
}

export interface RevealLogEntry {
  at: number;
  text: string;
}

export interface GameView {
  gameId: string;
  roomId: string;
  mode: GameMode;
  source: DataSource;
  stage: GameStage;
  /** окончание текущего этапа */
  stageEndsAt: number | null;
  startAt: number;
  minuteMs: number;
  lockAt: number;
  /** закрытие целевой свечи */
  targetCloseAt: number;
  tick: number;
  p0: number;
  pot: Minor;
  currentBet: Minor;
  raisesLeft: number;
  toActPlayerId: string | null;
  turnEndsAt: number | null;
  bettingClosed: boolean;
  seats: SeatView[];
  me: {
    participant: boolean;
    forecast: Forecast;
    allowed: AllowedActions | null;
    autoAction: AutoAction;
    toCall: Minor;
  };
  composition: Composition[] | null;
  commitment: CommitmentView;
  revealLog: RevealLogEntry[];
  /** лента ходов всех участников (последние записи) */
  actions: GameActionView[];
  settlement: SettlementView | null;
  /** пакет проверки доступен участнику после расчёта */
  verificationAvailable: boolean;
  commissionBps: number;
  ante: Minor;
}

export interface SettlementPlayerView {
  playerId: string;
  name: string;
  folded: boolean;
  direction: Direction | null;
  points: number | null;
  /** |прогноз − |Δ||, пункты; null если направление неверное/нет прогноза */
  error: number | null;
  correctDirection: boolean;
  contributed: Minor;
  payout: Minor;
  refund: Minor;
  net: Minor;
}

export interface PotView {
  index: number;
  amount: Minor;
  commission: Minor;
  eligible: string[];
  refunded: boolean;
  shares: { playerId: string; weight: string; amount: Minor }[];
}

export interface SettlementView {
  status: 'completed' | 'cancelled';
  kind: 'market' | 'walkover' | 'cancel';
  reason: string | null;
  p0: number;
  p1: number | null;
  delta: number | null;
  composition: Composition[];
  players: SettlementPlayerView[];
  pots: PotView[];
  commissionByRound: { round: number; newContributions: Minor; commission: Minor; cap: Minor | null }[];
  commissionTotal: Minor;
  bankDelta: Minor;
  totals: { debited: Minor; paid: Minor; refunded: Minor; bank: Minor; balanced: boolean };
  commitment: { hash: string; payload: string; salt: string } | null;
  settledAt: number;
}

// ───────────────────────── Лобби / комнаты ─────────────────────────

export interface TableCategory {
  id: string;
  title: string;
  ante: Minor;
  minPlayers: number;
  maxPlayers: number;
}

export type RoomStatus = 'forming' | 'countdown' | 'playing' | 'results';

export interface RoomSummary {
  roomId: string;
  categoryId: string;
  ante: Minor;
  status: RoomStatus;
  seated: number;
  ready: number;
  maxPlayers: number;
  minPlayers: number;
  /** старт следующего розыгрыша, если назначен */
  nextStartAt: number | null;
  /** текущий этап, если идёт розыгрыш */
  stage: GameStage | null;
  spectators: number;
}

export interface RoomSeatView {
  playerId: string;
  name: string;
  isBot: boolean;
  isMe: boolean;
  ready: boolean;
  inGame: boolean;
  balance: Minor;
  connected: boolean;
  /** открытая статистика игрока (если он разрешил её показывать) */
  stats: PublicStats | null;
}

export interface RoomView extends RoomSummary {
  seats: RoomSeatView[];
  meSeated: boolean;
  meReady: boolean;
  gamesPlayed: number;
  /** служебное сообщение стола (например, причина, по которой розыгрыш не начат) */
  notice: string | null;
}

export interface PlayerStats {
  games: number;
  wins: number;
  cancelled: number;
  folds: number;
  net: Minor;
  restores: number;
  /** средняя ошибка прогноза при верном направлении, пункты */
  avgError: number | null;
  correctDirection: number;
  /** доля розыгрышей с выплатой среди состоявшихся (без отменённых), 0–1; null — ещё не было */
  winRate: number | null;
  /** наибольший чистый выигрыш за розыгрыш */
  bestWin: Minor;
}

/** Статистика на плашке игрока за столом — только с его разрешения. */
export interface PublicStats {
  games: number;
  winRate: number | null;
  net: Minor;
}

export interface PlayerProfile {
  id: string;
  name: string;
  balance: Minor;
  stats: PlayerStats;
  canRestore: boolean;
  seatedRoomId: string | null;
  /** показывать винрейт и итог на плашке за столом */
  showStats: boolean;
}

// ───────────────────────── Учётные записи, история, администрирование (HTTP API) ─────────────────────────

export interface AuthProviderInfo {
  id: string;
  title: string;
}

/** Ответ на вход и регистрацию: POST /api/auth/login, /api/auth/register. */
export interface AuthResult {
  token: string;
  role: 'player' | 'admin';
}

export interface LoginMethods {
  /** логин для входа по паролю; null — пароль не задан */
  login: string | null;
  providers: { id: string; display: string | null; email: string | null; linkedAt: number }[];
}

/** Профиль с подробностями: GET /api/me, GET /api/admin/users/:id. */
export interface ProfileDetails {
  id: string;
  name: string;
  isBot: boolean;
  createdAt: number;
  lastSeenAt: number | null;
  bannedAt: number | null;
  banReason: string | null;
  showStats: boolean;
  balance: Minor;
  stats: PlayerStats;
  methods: LoginMethods;
  /** чистый результат по дням (последние 30 дней с игрой), для графика */
  daily: { day: string; games: number; net: Minor }[];
  online: boolean;
}

export interface GameListItem {
  gameId: string;
  roomId: string;
  categoryId: string;
  ante: Minor;
  mode: GameMode;
  source: DataSource;
  startedAt: number;
  settledAt: number;
  status: 'completed' | 'cancelled';
  kind: 'market' | 'walkover' | 'cancel';
  reason: string | null;
  delta: number | null;
  pot: Minor;
  commission: Minor;
  players: number;
  humans: number;
  composition: string[];
  /** результат игрока, для которого составлен список */
  me: { direction: Direction | null; points: number | null; folded: boolean; correct: boolean; error: number | null; contributed: Minor; net: Minor } | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}

/** Запись хронологии розыгрыша (публичная часть: ходы, этапы, раскрытия). */
export interface ReplayEvent {
  seq: number;
  at: number;
  round: number;
  kind: 'action' | 'autoFold' | 'uncalledReturn' | 'roundClosed' | 'stage' | 'reveal';
  playerId: string | null;
  name: string | null;
  action?: ActionType | 'ante';
  amount?: Minor;
  auto?: boolean;
  text: string;
}

export interface ForecastChangeView {
  at: number;
  direction: Direction | null;
  points: number | null;
}

/** Запись розыгрыша для повтора: GET /api/games/:id/replay (участнику или администратору). */
export interface GameReplay {
  gameId: string;
  roomId: string;
  categoryId: string;
  ante: Minor;
  mode: GameMode;
  source: DataSource;
  startAt: number;
  minuteMs: number;
  tick: number;
  p0: number;
  /** бары агрегата в пунктах, включая целевую свечу */
  bars: Bar[];
  seats: { playerId: string; name: string; isBot: boolean; order: number }[];
  events: ReplayEvent[];
  settlement: SettlementView;
  /** история изменений прогноза: свой — участнику, все — администратору */
  forecasts: Record<string, ForecastChangeView[]>;
  /** запись перенесена из журнала прежней версии: хронологии и графика нет */
  legacy: boolean;
}

export interface AdminOverview {
  generatedAt: number;
  users: { total: number; new24h: number; new7d: number; banned: number; online: number; active7d: number; withPassword: number; withSso: number };
  games: { total: number; completed: number; cancelled: number; last24h: number; avgPlayers: number | null; humanShare: number | null };
  credits: { bank: Minor; commissionTotal: Minor; granted: Minor; inPlay: Minor; potTotal: Minor };
  rooms: { open: number; playing: number; seated: number; spectators: number };
  byCategory: { categoryId: string; ante: Minor; games: number; commission: Minor; pot: Minor }[];
  daily: { day: string; games: number; commission: Minor; players: number }[];
  topPlayers: { id: string; name: string; games: number; net: Minor; winRate: number | null }[];
}

export interface AdminUserRow {
  id: string;
  name: string;
  login: string | null;
  providers: string[];
  createdAt: number;
  lastSeenAt: number | null;
  bannedAt: number | null;
  banReason: string | null;
  balance: Minor;
  games: number;
  winRate: number | null;
  net: Minor;
  online: boolean;
}

export interface ServerInfo {
  mode: GameMode;
  source: DataSource;
  sourceNote: string | null;
  stageMs: number;
  turnMs: number;
  commissionBps: number;
  /** потолок комиссии одного круга в долях взноса N стола; null — без потолка */
  commissionCapAntesPerRound: number | null;
  tick: number;
  indexBase: number;
  startingBalance: Minor;
  categories: TableCategory[];
  bankAccount: Minor;
  /** способы входа: провайдеры SSO, для которых на сервере заданы ключи */
  auth: { providers: AuthProviderInfo[] };
}

// ───────────────────────── Сообщения ─────────────────────────

export type ClientMessage =
  | { type: 'auth'; token: string }
  | { type: 'lobby.subscribe' }
  | { type: 'room.join'; categoryId: string }
  | { type: 'room.watch'; roomId: string }
  | { type: 'room.leave' }
  | { type: 'room.ready'; ready: boolean }
  | { type: 'game.action'; actionId: string; action: ActionType; amount?: Minor }
  | { type: 'game.forecast'; direction?: Direction | null; points?: number | null }
  | { type: 'game.auto'; auto: AutoAction }
  | { type: 'account.restore' }
  | { type: 'ping'; t: number };

export type ServerMessage =
  | { type: 'hello'; protocol: number; serverNow: number; info: ServerInfo }
  | { type: 'auth.ok'; role: 'player'; profile: PlayerProfile }
  | { type: 'auth.ok'; role: 'admin'; login: string }
  | { type: 'auth.error'; message: string }
  | { type: 'profile'; profile: PlayerProfile }
  | { type: 'lobby'; rooms: RoomSummary[]; bankAccount: Minor; serverNow: number }
  | { type: 'room'; room: RoomView | null; game: GameView | null; serverNow: number }
  | { type: 'chart'; chart: ChartPayload }
  | { type: 'ack'; actionId: string; ok: boolean; error?: string }
  | { type: 'error'; message: string }
  | { type: 'pong'; t: number; serverNow: number };

// ───────────────────────── Вспомогательное ─────────────────────────

export function formatCredits(minor: Minor): string {
  const sign = minor < 0 ? '−' : '';
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / MINOR_PER_CREDIT);
  const frac = abs % MINOR_PER_CREDIT;
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${sign}${wholeStr}${frac ? ',' + frac.toString().padStart(2, '0') : ''} кр.`;
}

export function creditsToMinor(credits: number): Minor {
  return Math.round(credits * MINOR_PER_CREDIT);
}
